// src/market/warehouse-baserow/useAdminReservation.js
import { useEffect, useState, useCallback } from "react";
import { useConfig } from "../../utensils/ConfigContext";

const useAdminReservation = () => {
  const { apiToken } = useConfig();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAllPages = async (url, accum = []) => {
    const res = await fetch(url, {
      headers: { Authorization: `Token ${apiToken}` },
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Error al obtener reservas: ${res.status} - ${text}`);
    }

    const data = await res.json();
    const combined = [...accum, ...data.results];

    if (data.next) {
      return fetchAllPages(data.next, combined);
    }

    return combined;
  };

  const refetch = useCallback(
    async (email) => {
      setLoading(true);
      setError(null);

      try {
        const filters = {
          filter_type: "AND",
          filters: [
            {
              type: "date_is",
              field: "Creado en",
              value: "America/Bogota??this_week",
            },
            {
              type: "equal",
              field: "Email",
              value: email,
            },
          ],
          groups: [],
        };

        const encodedFilters = encodeURIComponent(JSON.stringify(filters));

        const url = `https://api.baserow.io/api/database/rows/table/569475/?user_field_names=true&filters=${encodedFilters}`;

        const allData = await fetchAllPages(url);
        setReservations(allData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [apiToken]
  );

  // 🔎 NUEVO: igual que refetch pero aceptando `search` (sin tocar el refetch original)
  const refetchWithSearch = useCallback(
    async (email, search) => {
      setLoading(true);
      setError(null);

      try {
        const filters = {
          filter_type: "AND",
          filters: [
            {
              type: "date_is",
              field: "Creado en",
              value: "America/Bogota??this_week",
            },
            {
              type: "equal",
              field: "Email",
              value: email,
            },
          ],
          groups: [],
        };

        const encodedFilters = encodeURIComponent(JSON.stringify(filters));
        let url = `https://api.baserow.io/api/database/rows/table/569475/?user_field_names=true&filters=${encodedFilters}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }

        const allData = await fetchAllPages(url);
        setReservations(allData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [apiToken]
  );

  const deleteReservation = async (id) => {
    try {
      const res = await fetch(
        `https://api.baserow.io/api/database/rows/table/569475/${id}/?user_field_names=true`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Token ${apiToken}`,
          },
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error al eliminar reserva: ${res.status} - ${text}`);
      }

      return true;
    } catch (error) {
      console.error("deleteReservation error:", error.message);
      throw error;
    }
  };

  // ✅ Obtener reservas por clase
  const getReservationsByClassId = async (classId, className) => {
    const filter = {
      filter_type: "AND",
      filters: [
        { field: "Clase", type: "equal", value: classId },
        ...(className
          ? [{ field: "Nombre", type: "equal", value: className }]
          : []),
      ],
    };

    const url = new URL(
      "https://api.baserow.io/api/database/rows/table/569475/?user_field_names=true"
    );
    url.searchParams.append("filters", JSON.stringify(filter));

    const res = await fetch(url.toString(), {
      headers: {
        Authorization: `Token ${apiToken}`,
      },
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Error al filtrar reservas: ${res.status} - ${text}`);
    }

    const data = await res.json();
    return data.results;
  };

  // 🔎 NUEVO: búsqueda libre con `search` (opcionalmente con filtros extra)
  const searchReservations = async (searchText, extraFilters = null) => {
    const url = new URL(
      "https://api.baserow.io/api/database/rows/table/569475/?user_field_names=true"
    );
    if (extraFilters) {
      url.searchParams.append("filters", JSON.stringify(extraFilters));
    }
    if (searchText) {
      url.searchParams.append("search", searchText);
    }
    return await fetchAllPages(url.toString());
  };

  return {
    reservations,
    loading,
    error,
    refetch,
    refetchWithSearch, // 👈 añadido
    deleteReservation,
    getReservationsByClassId, // ✅ exportado
    searchReservations, // 👈 añadido
  };
};

export default useAdminReservation;

/**
 * 🔹 EXPORT NUEVO (nombrado): descarga TODAS las reservas paginando automáticamente.
 * No toca el hook ni su estado; es una utilidad pura para reportes.
 */
export const getAllReservations = async ({
  apiToken,
  filters = null,
  search = "",
  pageSize = 200,
} = {}) => {
  if (!apiToken) throw new Error("Falta apiToken para getAllReservations");

  const buildUrl = () => {
    const url = new URL(
      "https://api.baserow.io/api/database/rows/table/569475/"
    );
    url.searchParams.set("user_field_names", "true");
    if (pageSize) url.searchParams.set("size", String(pageSize));
    if (filters) url.searchParams.set("filters", JSON.stringify(filters));
    if (search) url.searchParams.set("search", search);
    return url.toString();
  };

  const headers = { Authorization: `Token ${apiToken}` };

  let next = buildUrl();
  const all = [];

  while (next) {
    const res = await fetch(next, { headers });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Error al obtener reservas: ${res.status} - ${text}`);
    }
    const data = await res.json();
    all.push(...(data.results || []));
    next = data.next || null;
  }

  return all;
};
